import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useHubstripe } from '../../hooks/useHubstripe'
import { RootState } from '../../store'
import { TBilling } from '../../shared/types/billing.type'
import { Alert, Box, Stack, Grid } from '@mui/material'
import { t } from 'i18next'
import BillingsTable from '../../components/user/billings/BillingsTable'
import BillingPaymentMethods from '../../components/user/billings/BillingPaymentMethods'
import BillingMessage from '../../components/user/billings/BillingMessage'
import BillingInformations from '../../components/user/billings/BillingInformations'
import BillingSubscriptions from '../../components/user/billings/BillingSubscriptions'
import BillingInvoices from '../../components/user/billings/BillingInvoices'
import BillingQuotes from '../../components/user/billings/BillingQuotes'
import { Navigate } from 'react-router-dom'
import { get, isEmpty } from 'lodash'
import { routes } from '../routes'
import BillingSkeleton from '../../components/user/billings/BillingSkeleton'
import { toast } from "react-toastify"

interface BillingsState {
    company: any;
    subscriptions: any;
    subscriptionSchedules: any;
    invoices: any;
    creditNotes: any;
    cegidDocs: any;
    customer: any;
    paymentMethods: any;
    deals: any;
}

const initialState: BillingsState = {
    company: null,
    subscriptions: null,
    subscriptionSchedules: null,
    invoices: null,
    creditNotes: null,
    cegidDocs: null,
    customer: null,
    paymentMethods: null,
    deals: null,
};

function Billings() {
    const [state, setState] = useState<BillingsState>(initialState);
    const [initialLoading, setInitialLoading] = useState(true);
    const [updatingPaymentMethods, setUpdatingPaymentMethods] = useState(false);

    const billings = useSelector<RootState>(state => state.user.user?.user_billing_infos) as TBilling[];

    const {
        getCompanyFromCustomerId,
        getDealsForCompanyId,
        getSubscriptionsFromCustomerId,
        getInvoicesFromCustomerId,
        getCreditNotesFromCustomerId,
        getCustomerFromCustomerId,
        getPaymentMethodsFromCustomerId
    } = useHubstripe();

    const fetchData = useCallback(async (customerId: string) => {
        setInitialLoading(true);
        try {
            const jsonCompany = await getCompanyFromCustomerId.mutateAsync(customerId);
            const jsonDeals = await getDealsForCompanyId.mutateAsync(jsonCompany.data.company.id);
            const jsonSubscriptions = await getSubscriptionsFromCustomerId.mutateAsync(customerId);
            const jsonInvoices = await getInvoicesFromCustomerId.mutateAsync(customerId);
            const jsonCreditNotes = await getCreditNotesFromCustomerId.mutateAsync(customerId);
            const jsonCustomer = await getCustomerFromCustomerId.mutateAsync(customerId);
            const jsonPaymentMethods = await getPaymentMethodsFromCustomerId.mutateAsync(customerId);

            setState({
                company: jsonCompany.data.company,
                subscriptions: jsonSubscriptions.data.subscriptions,
                subscriptionSchedules: jsonSubscriptions.data.subscriptionSchedules,
                invoices: jsonInvoices.data.invoices,
                creditNotes: jsonCreditNotes.data.creditNotes,
                cegidDocs: jsonInvoices.data.pieces,
                customer: jsonCustomer.data.customer,
                paymentMethods: jsonPaymentMethods.data.paymentMethods.data,
                deals: jsonDeals.data
            });
        } catch (err) {
            console.error(err);
            toast.error(t('error:generic'));
        } finally {
            setInitialLoading(false);
        }
    }, []);

    const stripeId = get(billings, '[0].stripe_id');

    useEffect(() => {
        if (stripeId) {
            fetchData(stripeId);
        } else {
            setInitialLoading(false);
        }
    }, [stripeId, fetchData]);

    const onPaymentMethodsChange = () => {
        if (stripeId) fetchData(stripeId);
    };

    const fetchPaymentMethods = async () => {
        const customerId = state.customer?.id;
        if (!customerId) return;

        setUpdatingPaymentMethods(true);
        try {
            const response = await getPaymentMethodsFromCustomerId.mutateAsync(customerId);
            const methods = get(response, "data.paymentMethods.data", []);
            setState(prev => ({ ...prev, paymentMethods: methods }));
        } catch (e) {
            toast.error(t('error:generic'));
        } finally {
            setUpdatingPaymentMethods(false);
        }
    };

    if (isEmpty(billings)) {
        return <Navigate to={routes.DASHBOARD_ROUTES.PROFILE} replace />;
    }

    if (initialLoading) {
        return <BillingSkeleton />;
    }

    if (billings.length > 1) {
        return (
            <Stack spacing={2}>
                <Alert severity="info">{t('purchaseOrders:explanation')}</Alert>
                <BillingsTable billings={billings} />
            </Stack>
        );
    }

    return (
        <Box width="100%">
            <Grid container spacing={2}>
                <Grid item md={3} xs={12}>
                    <Stack spacing={2} direction="column">
                        <BillingMessage invoices={state.invoices} />
                        <BillingInformations customer={state.customer} />
                        <BillingPaymentMethods
                            customer={state.customer}
                            paymentMethods={state.paymentMethods}
                            onPaymentMethodsChange={onPaymentMethodsChange}
                            loadingPaymentMethods={updatingPaymentMethods}
                            onSuccess={fetchPaymentMethods}
                        />
                    </Stack>
                </Grid>
                <Grid item md={9} xs={12}>
                    <Stack spacing={2} direction="column">
                        <BillingSubscriptions
                            subscriptions={state.subscriptions}
                            subscriptionSchedules={state.subscriptionSchedules}
                        />
                        <BillingInvoices
                            invoices={state.invoices}
                            creditNotes={state.creditNotes}
                            cegidDocs={state.cegidDocs}
                            paymentMethods={state.paymentMethods}
                            customer={state.customer}
                        />
                        <BillingQuotes deals={state.deals} />
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}

export default Billings;